import React, { FC, useEffect, useState } from "react";

// UI
import { Box } from "@UI/Components/Box/Box";
import { Stack } from "@UI/Components/Stack/Stack";

// Component

// Messages
import messages from "./messages";

// i18n
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

// hooks
import { ResponsiveFixedButton } from "@UI/Components/ResponsiveFixedButton/ResponsiveFixedButton";
import { StripeCreditCardForm } from "../../../Adapters/Stripe/StripeCreditCardForm";
import { useSetupIntent } from "@modules/PaymentProcessing/modules/CreditCard/hooks/stripe/useSetupIntent";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { useUserPaymentMethods } from "@modules/PaymentProcessing/hooks/useUserPaymentMethods";
import { CreditCardPreview } from "../../../CreditCardPreview/CreditCardPreview";
import { withStripe } from "@modules/PaymentProcessing/providers/stripe/hocs/withStripe";

interface Props {
  onSubmit?: (arg: string) => void;
  setIsServerError?: (arg: boolean) => void;
  children?: React.ReactNode;
}

export const ConfigureCreditCard: FC<Props> = withStripe(
  ({ onSubmit, setIsServerError, children }) => {
    const { getPreferredCreditCard, isSuccess: paymentMethodsFetched } =
      useUserPaymentMethods();
    const [preferredCreditCard, setPreferredCreditCard] = useState(undefined);
    const [name, setName] = useState("");
    const [formCompleted, setFormCompleted] = useState(false);
    const { submit, stripeError, cleanError, setStripeError, isLoading } =
      useSetupIntent({
        name,
        onSuccess: () => onSubmit("stripe"),
        onError: () => setIsServerError(true),
        enabled: !(paymentMethodsFetched && preferredCreditCard),
      });

    const handleSubmit = (e) => {
      e.preventDefault();
      if (preferredCreditCard) {
        onSubmit("stripe");
      } else {
        submit.mutate();
      }
    };

    const handleNameChange = (name: string) => {
      setName(name);
      cleanError();
    };

    const handleCompleted = () => {
      setFormCompleted(true);
    };

    useEffect(() => {
      setPreferredCreditCard(getPreferredCreditCard());
    }, [paymentMethodsFetched]);

    if (isLoading || !paymentMethodsFetched) {
      return <LoadingScreen />;
    }

    const isButtonDisabled =
      !preferredCreditCard &&
      (!formCompleted || !!stripeError || submit.isLoading);

    return (
      <Stack spacing={5}>
        {preferredCreditCard ? (
          <CreditCardPreview
            paymentMethod={preferredCreditCard}
            onChangeCard={() => setPreferredCreditCard(undefined)}
          />
        ) : (
          <StripeCreditCardForm
            onNameChange={handleNameChange}
            setStripeError={setStripeError}
            stripeError={stripeError}
            cleanError={cleanError}
            onCompleted={handleCompleted}
          />
        )}

        {children}
        <Box>
          <ResponsiveFixedButton
            data-testid="submit-credit-card-button"
            isLoading={submit.isLoading}
            h={12}
            width="full"
            colorScheme="seaGreen"
            onClick={handleSubmit}
            isDisabled={isButtonDisabled}
          >
            <FormattedMessage {...messages.submit} />
          </ResponsiveFixedButton>
        </Box>
      </Stack>
    );
  }
);
